import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import {
    DropdownItem,
    DropdownMenu,
    HorizontalContainer,
    TextInput,
    useEvent
} from '@american-autonomy/ag-ui';

import DropdownText from 'components/text/DropdownText';

import useTranslation from './hooks/useTranslation';

import { TEST_ID } from './constants';
import type { Props } from './types';

import styles from './styles.module.scss';

const Combobox = forwardRef<HTMLInputElement, Props>((props, ref) => {
    const { ERROR_OCCURRED } = useTranslation();
    const {
        asyncFilter,
        className = '',
        debounceTime = 300,
        isDisabled = false,
        isEditable = true,
        isLoading,
        isRequired = false,
        items = [],
        name = '',
        noResultsText = 'No results found',
        onChange,
        onError,
        onValid,
        placeholder = '',
        testId = TEST_ID,
        value = ''
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [searchValue, setSearchValue] = useState(value);
    const [isListLoading, setIsListLoading] = useState(isLoading);
    const [filteredItems, setFilteredItems] = useState<typeof items>([]);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const handleAsyncFilter = useCallback(
        async (searchTerm: string) => {
            if (!asyncFilter) {
                return;
            }

            try {
                setIsListLoading(true);
                const results = await asyncFilter(searchTerm);

                setFilteredItems(results || []);
            } catch (error) {
                onError?.(error instanceof Error ? error.message : ERROR_OCCURRED);
            } finally {
                setIsListLoading(false);
            }
        },
        [ERROR_OCCURRED, asyncFilter, onError]
    );

    const handleChange = useCallback(
        (newValue: string) => {
            setSearchValue(newValue);
            setIsOpen(true);

            if (asyncFilter) {
                if (debounceTimeout.current) {
                    clearTimeout(debounceTimeout.current);
                }

                debounceTimeout.current = setTimeout(() => {
                    handleAsyncFilter(newValue);
                }, debounceTime);
            } else {
                const lowercasedNewValue = newValue.toLowerCase();

                setFilteredItems(
                    items.filter(
                        ({ label, value }) =>
                            label.toLowerCase().includes(lowercasedNewValue) ||
                            value.toLowerCase().includes(lowercasedNewValue)
                    )
                );
            }

            onChange?.(newValue);
        },
        [asyncFilter, debounceTime, handleAsyncFilter, items, onChange]
    );

    const handleItemClick = useCallback(
        (itemValue: string, itemLabel: string) => {
            setSearchValue(itemLabel);
            setIsOpen(false);
            onValid?.(itemValue);
            onChange?.(itemValue);
        },
        [onChange, onValid]
    );

    const handleClickOutside = useCallback((event: Event) => {
        const target = event.target as HTMLElement;
        const isClickInside = containerRef.current?.contains(target);

        if (!isClickInside) {
            setIsOpen(false);
        }
    }, []);

    const handleInputClick = useCallback(() => {
        setIsOpen(true);
    }, []);

    useEvent({ event: 'mousedown', handler: handleClickOutside });

    useEffect(
        () => () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        },
        []
    );

    useEffect(() => {
        setSearchValue(value);
    }, [value]);

    return (
        <HorizontalContainer
            className={`${className} ${styles.combobox}`}
            ref={containerRef}
            testId={testId}>
            <TextInput
                className={`${styles.textInput} ${className}`}
                isDisabled={isDisabled}
                isEditable={isEditable}
                isRequired={isRequired}
                name={name}
                onChange={handleChange}
                onClick={handleInputClick}
                placeholder={placeholder}
                ref={ref || inputRef}
                value={searchValue}
            />

            {isOpen && (
                <DropdownMenu
                    className={styles.dropdownMenu}
                    inputRef={containerRef}
                    onClose={() => setIsOpen(false)}>
                    {(isListLoading || isLoading) && (
                        <DropdownItem className={styles.dropdownItem}>Loading...</DropdownItem>
                    )}

                    {filteredItems.length === 0 && items.length === 0 && !isLoading && (
                        <DropdownItem className={styles.dropdownItem}>
                            <DropdownText>{noResultsText}</DropdownText>
                        </DropdownItem>
                    )}

                    {filteredItems.length > 0
                        ? filteredItems.map(({ label, value: itemValue }) => (
                              <DropdownItem
                                  className={styles.dropdownItem}
                                  key={itemValue}
                                  onClick={() => handleItemClick(itemValue, label)}>
                                  <DropdownText>{label}</DropdownText>
                              </DropdownItem>
                          ))
                        : items.map(({ label, value: itemValue }) => (
                              <DropdownItem
                                  className={styles.dropdownItem}
                                  key={itemValue}
                                  onClick={() => handleItemClick(itemValue, label)}>
                                  <DropdownText>{label}</DropdownText>
                              </DropdownItem>
                          ))}
                </DropdownMenu>
            )}
        </HorizontalContainer>
    );
});

Combobox.displayName = 'Combobox';

export default Combobox;
